/**
 * Production Workflow Tracker — Visual pipeline from Lead to Handover
 */
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Activity, Check, Clock, ArrowRight, User } from 'lucide-react';
import { toast } from 'sonner';

const STAGES = [
  { id: 'lead', label: 'Lead', color: 'bg-stone-400' },
  { id: 'estimate', label: 'Estimate', color: 'bg-blue-500' },
  { id: 'approved', label: 'Approved', color: 'bg-green-500' },
  { id: 'cutting', label: 'Cutting', color: 'bg-amber-500' },
  { id: 'edge_banding', label: 'Edge Band', color: 'bg-orange-500' },
  { id: 'lamination', label: 'Lamination', color: 'bg-rose-500' },
  { id: 'cnc', label: 'CNC', color: 'bg-purple-500' },
  { id: 'assembly', label: 'Assembly', color: 'bg-indigo-500' },
  { id: 'dispatch', label: 'Dispatch', color: 'bg-cyan-500' },
  { id: 'installation', label: 'Installation', color: 'bg-teal-500' },
  { id: 'handover', label: 'Handover', color: 'bg-emerald-600' },
];

function getStorageKey(estimateId) {
  return `bellacasa_workflow_${estimateId || 'new'}`;
}

export default function ProductionWorkflow({ estimateId, estimateName }) {
  const [workflow, setWorkflow] = useState(() => {
    try {
      const saved = localStorage.getItem(getStorageKey(estimateId));
      return saved ? JSON.parse(saved) : {
        currentStage: 'estimate',
        history: [{ stage: 'estimate', at: new Date().toISOString(), by: 'System' }],
        owners: {},
      };
    } catch { return { currentStage: 'estimate', history: [], owners: {} }; }
  });

  useEffect(() => {
    localStorage.setItem(getStorageKey(estimateId), JSON.stringify(workflow));
  }, [workflow, estimateId]);

  const currentIdx = STAGES.findIndex(s => s.id === workflow.currentStage);

  const advanceStage = () => {
    if (currentIdx >= STAGES.length - 1) { toast.info('Already at final stage'); return; }
    const nextStage = STAGES[currentIdx + 1];
    const updated = {
      ...workflow,
      currentStage: nextStage.id,
      history: [...workflow.history, { stage: nextStage.id, at: new Date().toISOString(), by: 'Admin' }],
    };
    setWorkflow(updated);
    toast.success(`Advanced to: ${nextStage.label}`);
  };

  const goToStage = (stageId) => {
    const idx = STAGES.findIndex(s => s.id === stageId);
    if (idx < 0) return;
    const updated = {
      ...workflow,
      currentStage: stageId,
      history: [...workflow.history, { stage: stageId, at: new Date().toISOString(), by: 'Admin (manual)' }],
    };
    setWorkflow(updated);
    toast.success(`Moved to: ${STAGES[idx].label}`);
  };

  const setOwner = (stageId, owner) => {
    setWorkflow(prev => ({ ...prev, owners: { ...prev.owners, [stageId]: owner } }));
  };

  const getStageTime = (stageId) => {
    const entry = [...workflow.history].reverse().find(h => h.stage === stageId);
    return entry ? new Date(entry.at).toLocaleString() : null;
  };

  return (
    <div className="space-y-5" data-testid="production-workflow">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Activity size={18} className="text-teal-600" /><h3 className="font-bold text-stone-800">Production Workflow</h3></div>
        <Button size="sm" onClick={advanceStage} disabled={currentIdx >= STAGES.length - 1} data-testid="advance-stage-btn">
          <ArrowRight size={14} className="mr-1" />
          {currentIdx < STAGES.length - 1 ? `Advance to ${STAGES[currentIdx + 1]?.label}` : 'Complete'}
        </Button>
      </div>

      {/* Visual Pipeline */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-1 overflow-x-auto pb-2">
            {STAGES.map((stage, idx) => {
              const isPast = idx < currentIdx;
              const isCurrent = idx === currentIdx;
              const isFuture = idx > currentIdx;
              return (
                <div key={stage.id} className="flex items-center flex-shrink-0">
                  <button
                    onClick={() => goToStage(stage.id)}
                    className={`flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg transition-all min-w-[70px] ${
                      isCurrent ? `${stage.color} text-white shadow-md scale-105` :
                      isPast ? 'bg-green-100 text-green-800' :
                      'bg-stone-100 text-stone-400'
                    }`}
                    data-testid={`stage-${stage.id}`}
                  >
                    {isPast ? <Check size={14} /> : isCurrent ? <Clock size={14} /> : <div className="w-3.5 h-3.5 rounded-full border-2 border-current" />}
                    <span className="text-[10px] font-bold">{stage.label}</span>
                  </button>
                  {idx < STAGES.length - 1 && (
                    <div className={`w-4 h-0.5 flex-shrink-0 ${isPast ? 'bg-green-400' : 'bg-stone-200'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Stage Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {STAGES.map((stage, idx) => {
          const isPast = idx < currentIdx;
          const isCurrent = idx === currentIdx;
          const time = getStageTime(stage.id);
          return (
            <Card key={stage.id} className={isCurrent ? 'ring-2 ring-blue-400' : isPast ? 'opacity-70' : 'opacity-40'}>
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${isPast ? 'bg-green-500' : isCurrent ? stage.color : 'bg-stone-300'}`} />
                    <span className="font-bold text-sm text-stone-800">{stage.label}</span>
                    {isCurrent && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-bold">Current</span>}
                    {isPast && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-bold">Done</span>}
                  </div>
                  {time && <span className="text-[10px] text-stone-400">{time}</span>}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <User size={12} className="text-stone-400" />
                  <input
                    className="h-7 text-xs border border-stone-200 rounded px-2 flex-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    placeholder="Assign owner..."
                    value={workflow.owners[stage.id] || ''}
                    onChange={e => setOwner(stage.id, e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* History Log */}
      {workflow.history.length > 0 && (
        <Card>
          <CardContent className="pt-4 pb-3">
            <h4 className="font-semibold text-sm text-stone-700 mb-2">Activity Log</h4>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {[...workflow.history].reverse().map((entry, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-stone-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-stone-400 flex-shrink-0" />
                  <span className="font-medium">{STAGES.find(s => s.id === entry.stage)?.label || entry.stage}</span>
                  <span className="text-stone-400">by {entry.by}</span>
                  <span className="text-stone-300 ml-auto">{new Date(entry.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
